import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Catches a render error so one broken page does not blank the whole app.
 * Give it a `resetKey` (the route) so moving to another page clears the error.
 */
export default class ErrorBoundary extends Component<
  { children: ReactNode; resetKey?: string; label?: string },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[NL-AOS] render error", error, info.componentStack);
  }

  componentDidUpdate(prev: { resetKey?: string }) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="state-panel error-panel" role="alert">
        <div>
          <strong>{this.props.label || "This page hit a problem"}</strong>
          <p>
            Something on this page failed to display. The rest of NOLA still works. Try again, or
            open another page.
          </p>
        </div>
        <button className="button secondary" onClick={() => this.setState({ error: null })}>
          Try again
        </button>
      </div>
    );
  }
}
